import { Icon } from "@iconify/react";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const DoctorAllPatientsTable = ({ patient }) => {
  const [packages, setPackages] = useState(null);
  const issueDate = moment().format("YYYY-MM-DD");

  useEffect(() => {
    fetch(
      `https://servercareseebd.careseebd.com/api/v1/package/specific?fieldName=${"userId"}&&fieldValue=${patient?._id}`
    )
      .then((res) => res.json())
      .then((data) => {
        if (data?.data?.length) {
          setPackages(data?.data?.[0]);
        }
      });
  }, [patient?._id]);

  const isExpire = packages?.expireDate
    ? moment(issueDate).isAfter(packages?.expireDate)
    : true;

  // Format Date of Birth safely
  const dob = patient?.dateOfBirth
    ? moment(patient.dateOfBirth).format("DD MMM YYYY")
    : "Not Provided";

  // Create patient initials for avatar
  const initials = patient?.name
    ? patient.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()
    : "PT";

  return (
    <tr key={patient?._id} className="hover:bg-slate-50/70 transition duration-200 group">
      {/* 1. Patient Profile Info (Name & Initials) */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-3.5">
          {/* Avatar with dynamic indicator border */}
          <div className="relative flex-shrink-0">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-50 to-indigo-100/50 text-secondary border border-indigo-100 flex items-center justify-center font-bold text-sm tracking-wide shadow-sm group-hover:scale-105 duration-200">
              {initials}
            </div>
            {/* Tiny green status dot on avatar if patient is active */}
            {patient?.isActive && (
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white"></span>
            )}
          </div>
          <div>
            <span className="block font-bold text-slate-800 text-sm group-hover:text-secondary transition duration-155">
              {patient?.name}
            </span>
            <span className="block text-[11px] text-slate-400 font-semibold tracking-wider uppercase mt-0.5">
              ID: {patient?._id?.slice(-8) || "N/A"}
            </span>
          </div>
        </div>
      </td>

      {/* 2. Contact Details (Email & Phone) */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex flex-col">
          <span className="text-slate-700 font-semibold text-xs flex items-center gap-1">
            <Icon icon="solar:envelope-linear" className="text-slate-400" />
            {patient?.email}
          </span>
          <span className="text-slate-450 text-[11px] font-medium mt-1 flex items-center gap-1">
            <Icon icon="solar:phone-linear" className="text-slate-400" />
            {patient?.phone || "No phone number"}
          </span>
        </div>
      </td>

      {/* 3. Subscription/Therapy Plan */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex flex-col gap-1 items-start">
          <span className="text-xs font-bold text-slate-800 tracking-wide">
            {packages?.packageName || "No Plan Active"}
          </span>
          {packages ? (
            isExpire ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-100">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                <span>Expired: {packages?.expireDate}</span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                <span>Valid to: {packages?.expireDate}</span>
              </span>
            )
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold bg-slate-100 text-slate-500 border border-slate-200">
              <span>N/A</span>
            </span>
          )}
        </div>
      </td>

      {/* 4. Active/Inactive Status */}
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-extrabold ${patient?.isActive
          ? "bg-green-50 text-green-700 border border-green-200"
          : "bg-red-50 text-red-700 border border-red-200"
          }`}>
          <span className={`w-1.5 h-1.5 rounded-full ${patient?.isActive ? "bg-green-500" : "bg-red-500"}`}></span>
          {patient?.isActive ? "Active" : "Inactive"}
        </span>
      </td>

      {/* 5. Date of Birth */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-1.5 text-slate-600 font-semibold text-xs">
          <Icon icon="solar:calendar-linear" className="text-slate-400 text-sm" />
          <span>{dob}</span>
        </div>
      </td>

      {/* 6. Action Buttons */}
      <td className="px-6 py-4 whitespace-nowrap text-center">
        <div className="flex justify-center items-center gap-2">
          {/* View Clinical Report Button */}
          <Link
            to={`/doctorDashboard/PatientReport/${patient?._id}`}
            title="View Clinical Report"
            className="p-2 border border-slate-200 text-slate-400 hover:text-secondary hover:border-secondary hover:bg-indigo-50/50 rounded-xl transition duration-200 flex items-center justify-center shadow-sm"
          >
            <Icon icon="solar:graph-new-linear" className="text-lg" />
          </Link>

          {/* Edit Patient Profile Button */}
          <Link
            to={`/doctorDashboard/patient-edit-profile/${patient?._id}`}
            title="Edit Patient Profile"
            className="p-2 border border-slate-200 text-slate-400 hover:text-primary hover:border-primary hover:bg-cyan-50/50 rounded-xl transition duration-200 flex items-center justify-center shadow-sm"
          >
            <Icon icon="solar:pen-new-round-linear" className="text-lg" />
          </Link>
        </div>
      </td>
    </tr>
  );
};

export default DoctorAllPatientsTable;
